import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { GET_CHANNELS, GET_IMAGES } from '../graphql/queries';
import { CREATE_CHANNEL, UPDATE_CHANNEL, ROLLBACK_CHANNEL, DELETE_CHANNEL } from '../graphql/mutations';
import { DataTable } from '../components/DataTable';
import { toast } from 'sonner';

function CreateChannelDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [autoPromote, setAutoPromote] = useState(false);
  const [createChannel, { loading }] = useMutation(CREATE_CHANNEL);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createChannel({ variables: { input: { name, description, autoPromote } } });
      toast.success(`Channel "${name}" created`);
      onCreated();
      onClose();
      setName(''); setDescription(''); setAutoPromote(false);
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="card modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 500, margin: '100px auto' }}>
        <h3>Create Channel</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} required
              placeholder="e.g., latest, stable, hardened-2024Q1"
              style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #dfe6e9' }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Description</label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Optional description"
              style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #dfe6e9' }} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={autoPromote} onChange={e => setAutoPromote(e.target.checked)} />
              Auto-promote new builds
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="btn" style={{ background: '#dfe6e9', color: '#2d3436' }}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !name}>
              {loading ? 'Creating...' : 'Create Channel'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Channels() {
  const [showCreate, setShowCreate] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const { data, loading, refetch } = useQuery(GET_CHANNELS);
  const { data: imagesData } = useQuery(GET_IMAGES, { variables: { limit: 50 } });
  const [updateChannel] = useMutation(UPDATE_CHANNEL);
  const [rollbackChannel] = useMutation(ROLLBACK_CHANNEL);
  const [deleteChannel] = useMutation(DELETE_CHANNEL);

  const channels = data?.listChannels || [];
  const images = imagesData?.listImages?.items || [];

  const handleAssign = async (channelName, imageId) => {
    const image = images.find(i => i.id === imageId);
    try {
      await updateChannel({
        variables: { input: { name: channelName, imageId, amiId: image?.amiId } },
      });
      toast.success(`Channel "${channelName}" updated`);
      refetch();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRollback = async (channelName, timestamp) => {
    if (!window.confirm(`Rollback channel "${channelName}" to ${new Date(timestamp).toLocaleString()}?`)) return;
    try {
      await rollbackChannel({ variables: { name: channelName, toTimestamp: timestamp } });
      toast.success(`Channel "${channelName}" rolled back`);
      refetch();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (channelName) => {
    if (!window.confirm(`Delete channel "${channelName}"?`)) return;
    try {
      await deleteChannel({ variables: { name: channelName } });
      toast.success(`Channel "${channelName}" deleted`);
      setSelectedChannel(null);
      refetch();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const channelColumns = [
    { accessorKey: 'name', header: 'Channel', cell: ({ row }) => (
      <button className="link" onClick={() => setSelectedChannel(row.original)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
        {row.original.name}
      </button>
    )},
    { accessorKey: 'currentAmiId', header: 'Current AMI', cell: ({ getValue }) => getValue() ? <code style={{ fontSize: 12 }}>{getValue()}</code> : <span style={{ color: '#b2bec3' }}>None</span> },
    { accessorKey: 'autoPromote', header: 'Auto-Promote', cell: ({ getValue }) => getValue() ? <span className="badge badge-success">Yes</span> : <span className="badge badge-pending">No</span> },
    { accessorKey: 'updatedAt', header: 'Last Updated', cell: ({ getValue }) => getValue() ? new Date(getValue()).toLocaleString() : '-' },
    { id: 'assign', header: 'Actions', cell: ({ row }) => (
      <select onChange={(e) => { if (e.target.value) handleAssign(row.original.name, e.target.value); e.target.value = ''; }}
        style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #dfe6e9', fontSize: 12 }}>
        <option value="">Assign Image...</option>
        {images.map(img => <option key={img.id} value={img.id}>{img.name}</option>)}
      </select>
    )},
  ];

  const terraformSnippet = selectedChannel ? `# Terraform HTTP data source
data "http" "channel_ami" {
  url = "\${var.image_factory_url}/api/v1/channels/${selectedChannel.name}/ami?region=\${var.aws_region}"
  request_headers = {
    Authorization = "Bearer \${var.api_token}"
  }
}

locals {
  ami_id = jsondecode(data.http.channel_ami.response_body).ami_id
}

resource "aws_instance" "example" {
  ami           = local.ami_id
  instance_type = "t3.medium"
}` : '';

  return (
    <div>
      <h2 className="page-title">Channels</h2>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <p style={{ color: '#636e72', margin: 0 }}>
            Named distribution channels for golden images. Consumers reference a channel instead of an AMI ID.
          </p>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>Create Channel</button>
        </div>

        {loading ? (
          <div className="loading">Loading channels...</div>
        ) : (
          <DataTable columns={channelColumns} data={channels} pageSize={10} />
        )}
      </div>

      {selectedChannel && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>Channel: {selectedChannel.name}</h3>
            <button className="btn btn-danger" style={{ fontSize: 12, padding: '4px 12px' }} onClick={() => handleDelete(selectedChannel.name)}>Delete</button>
          </div>
          {selectedChannel.description && <p style={{ color: '#636e72' }}>{selectedChannel.description}</p>}

          {selectedChannel.history?.length > 0 && (
            <>
              <h4 style={{ marginTop: 16, marginBottom: 8 }}>History</h4>
              <table className="table">
                <thead><tr><th>Image ID</th><th>AMI ID</th><th>Promoted At</th><th>Actions</th></tr></thead>
                <tbody>
                  {selectedChannel.history.map((h, i) => (
                    <tr key={i}>
                      <td><a href={`/images/${h.imageId}`} className="link">{h.imageId.slice(0, 8)}...</a></td>
                      <td><code style={{ fontSize: 12 }}>{h.amiId || '-'}</code></td>
                      <td>{new Date(h.promotedAt).toLocaleString()}</td>
                      <td>
                        <button className="btn btn-primary" style={{ fontSize: 11, padding: '2px 8px' }}
                          onClick={() => handleRollback(selectedChannel.name, h.promotedAt)}>
                          Rollback
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <h4 style={{ marginTop: 16, marginBottom: 8 }}>Terraform Usage</h4>
          <div className="code-block">
            <pre><code>{terraformSnippet}</code></pre>
          </div>
        </div>
      )}

      <CreateChannelDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={refetch} />
    </div>
  );
}
